import { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isPlatformAdmin } from '../../utils/auth';
import AdminSidebar from './AdminSidebar';
import RouteErrorBoundary from './RouteErrorBoundary';
import RealtimeProvider from '../../realtime/RealtimeProvider';

/**
 * Dedicated chrome for /admin/* — separate from customer Layout + Sidebar.
 */
export default function AdminShell() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem('txnweb-sidebar-collapsed');
    setIsSidebarCollapsed(stored === '1');
  }, []);

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('txnweb-sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-txnweb-canvas">
        <div className="w-10 h-10 border-2 border-navy-700/20 border-t-navy-700 rounded-full animate-spin" aria-hidden />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isPlatformAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <RealtimeProvider>
      <div className={`min-h-screen bg-txnweb-canvas ${isSidebarCollapsed ? 'pl-20' : 'pl-64'}`}>
        <AdminSidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={handleToggleSidebar} />
        <main className="min-w-0 w-full max-w-full px-6 md:px-14 lg:px-20 xl:px-24 py-10 md:py-14 pb-24 min-h-screen">
          <div className="txnweb-main-inner">
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
    </RealtimeProvider>
  );
}
